/**
 * Bosnian strings for the fiveS screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 */

export const fiveS = {
  // ---- The walk-through --------------------------------------------
  'fiveS.title': '5S evaluacija',
  'fiveS.denied.title': 'Nemate pravo za 5S evaluaciju',
  'fiveS.denied.description': '5S ocjenu unose nadležni, komisija i administrator.',

  /*
   * The rules of the walk, said before it starts.
   *
   * Each of these is a refusal the server already makes, written out where
   * somebody can read it instead of running into it — the upsert key in
   * FiveSController::store, and the reopen that only an administrator may do.
   * The wording follows lang/bs/guide.php so the tooltip and the printed guide
   * cannot drift into saying two different things.
   */
  'fiveS.help.oneWalk':
    'Jedan obilazak po mašini, po danu, po smjeni. Jutarnji i noćni obilazak iste mašine istog dana su dva odvojena zapisa, jer opisuju dva različita stanja centra.',

  'fiveS.workCenter': 'Radni centar',
  'fiveS.date': 'Datum obilaska',
  'fiveS.date.help':
    'Upisujete dan kad ste stvarno obišli mašinu, a ne dan kad unosite ocjene — zapis opisuje stanje centra tog dana.',
  // The month is the server's word, interpolated rather than assembled here —
  // see /api/meta. Only the sentence around it is ours.
  'fiveS.periodHint': 'Evaluacija se vodi za {month} {year}.',
  'fiveS.shift': 'Smjena',
  'fiveS.list.allShifts': 'Sve smjene',
  'fiveS.shift.hint': 'Koja smjena je držala centar. Nije obavezno.',
  'fiveS.shift.help':
    'Aplikacija predlaže smjenu koja traje u ovom trenutku; ako pogon ne radi u smjenama, ovog polja nema.',
  // A named option, not a blank row: "no shift" is a thing somebody can mean.
  'fiveS.shift.none': 'Bez smjene',

  'fiveS.workers': 'Radnici na centru',
  'fiveS.workers.hint': 'Ko je održavao centar u ovom periodu. Nije obavezno.',
  // The half of the worker figure that gets misread: a number beside a name is
  // taken as a judgement of the person unless the screen says otherwise.
  'fiveS.workers.help':
    'Iz ovog spiska se računa 5S ocjena po radniku, a ona opisuje stanje mašine koju su držali, ne njih same.',

  'fiveS.template.loading': 'Učitavanje obrasca…',
  'fiveS.template.missing': 'Obrazac nije pronađen',

  'fiveS.onlyUnanswered': 'Prikaži samo neocijenjene ({count})',
  'fiveS.onlyUnanswered.hint':
    'Korisno pri kraju obilaska, da ne tražite propušteno po cijeloj listi.',

  'fiveS.comment': 'Napomena',
  'fiveS.comment.hint': 'Vidljiva rukovodstvu uz ocjenu.',
  'fiveS.comment.placeholder':
    'npr. Alat vraćen na mjesto, ali obilježavanje zona nije završeno.',

  /*
   * Counted phrases, written so the number can be any number.
   *
   * These sit in a running total that changes on every tap, so the noun after
   * the count would have to move through all three Slavic plural forms as the
   * evaluator works — "1 kriterijum", "2 kriterijuma", "5 kriterijuma". Writing
   * them as the counters they are — "12 / 15", "još 3" — leaves no noun for
   * the number to agree with.
   */
  'fiveS.progress': '{answered} / {total} ocijenjeno',
  // Short, because it shares a phone's width with "Sačuvaj nacrt"; the
  // screen reader hears where it goes, starting with what is on it.
  'fiveS.jump': 'Još {count}',
  'fiveS.jump.aria': 'Još {count}: idi na prvi neocijenjeni kriterijum',
  'fiveS.progressLabel': 'Ocijenjeni kriterijumi',
  'fiveS.percentage.help':
    'Procenat se koristi jer radni centri mogu imati različite skale, pa se sirovi zbirovi ne mogu porediti.',

  'fiveS.saveDraft': 'Sačuvaj nacrt',
  'fiveS.submit': 'Predaj',
  'fiveS.submit.hint':
    'Predaja je moguća tek kada su svi kriterijumi ocijenjeni. Nacrt možete sačuvati bilo kada.',
  'fiveS.submit.help':
    'Predata evaluacija se više ne mijenja jer je to zapis stanja mašine tog dana; ponovo je otvara samo administrator.',
  'fiveS.submit.blocked': 'Prije predaje ocijenite sve kriterijume. Neocijenjeno: {count}.',
  'fiveS.draftSaved': 'Nacrt je sačuvan.',
  'fiveS.submitted': 'Evaluacija je predata — {score}.',
  'fiveS.submitted.next': 'Evaluacija je predata — {score}. Izaberite sljedeći radni centar.',
  'fiveS.submitted.open': 'Otvori',
  'fiveS.saveFailed': 'Čuvanje nije uspjelo.',

  'fiveS.centerChange.title': 'Promijeniti radni centar?',
  'fiveS.centerChange.stay': 'Ostani ovdje',
  'fiveS.centerChange.confirm': 'Promijeni i obriši ocjene',
  'fiveS.centerChange.body':
    'Svaki radni centar ima svoj obrazac, pa se unesene ocjene ne mogu prenijeti. Broj unesenih ocjena: {count}. Promjenom radnog centra sve se brišu.',
  'fiveS.centerChange.keepHint':
    'Ako želite da ih sačuvate, prvo zatvorite ovaj prozor i pritisnite „Sačuvaj nacrt”.',

  // ---- The stickers on the machines --------------------------------
  'fiveS.stickers.title': 'QR naljepnice',
  'fiveS.stickers.subtitle':
    'Naljepnice za mašine. Radnik skenira kod i prijava se otvara sa već izabranim radnim centrom.',
  'fiveS.stickers.printHint':
    'Odštampajte na A4, isjecite po isprekidanoj liniji i plastificirajte. Zalijepite u visini očiju, dalje od prašine i ulja.',
  'fiveS.stickers.printAll': 'Štampaj sve',
  'fiveS.stickers.printSelected': 'Štampaj ({count})',
  'fiveS.stickers.clearSelection': 'Poništi izbor',
  'fiveS.stickers.centers': 'Radni centri ({count})',
  'fiveS.stickers.select': 'Izaberi {name}',
  'fiveS.stickers.hasCode': 'Izdat',
  'fiveS.stickers.noCode': 'Bez koda',
  'fiveS.stickers.regenerate': 'Obnovi',
  'fiveS.stickers.regenerateFor': 'Obnovi QR kod za {name}',
  // The line break belongs to the sentence: this is a browser confirm, and the
  // blank line is what separates the question from what it will break.
  'fiveS.stickers.regenerateConfirm':
    'Obnoviti QR kod za „{name}"?\n\nNaljepnica koja je trenutno na mašini prestaje da važi odmah. Moraćete odštampati i zalijepiti novu.',
  'fiveS.stickers.regenerated': 'QR kod je obnovljen.',
  'fiveS.stickers.regenerateFailed': 'Obnavljanje nije uspjelo.',

  // ---- The printed worker card -------------------------------------
  'fiveS.badge.preparing': 'Priprema kartice…',
  'fiveS.badge.alt': 'QR kartica za prijavu',
  'fiveS.badge.warning':
    'Kartica je kao ključ — ko je ima, može se prijaviti ovim nalogom, pa i fotografija kartice. Čuvajte je kao ličnu kartu. Ako se izgubi, izdajte novu; stara odmah prestaje da važi.',
  'header.newEvaluation': 'Nova 5S evaluacija',
  'fiveS.list.readOnly': '5S evaluacije obavljaju odgovorna lica — ovdje ih možete pregledati.',
  'fiveS.list.search': 'Traži po radnom centru ili ocjenjivaču…',
  'fiveS.list.allCentres': 'Svi radni centri',
  'fiveS.list.allStatuses': 'Svi statusi',
  'fiveS.list.status': 'Status',
  'fiveS.list.empty': 'Još nema nijedne 5S evaluacije.',
  'fiveS.list.workers': '{count} radnika',
  'fiveS.status.draft': 'Nacrt',
  'fiveS.status.submitted': 'Predano',
  'fiveS.detail.back': 'Nazad na 5S',
  'fiveS.detail.notScored': 'nije ocijenjeno',
  'fiveS.detail.edit': 'Izmijeni',

  // --- One walk, as charts -------------------------------------------
  //
  // The screen used to show a percentage and a list. A percentage on its own is
  // close to useless to whoever has to act on it: 72% is good news after 60%
  // and bad news after 85%.
  'fiveS.insights.categories.title': 'Pet S po kategorijama',
  'fiveS.insights.categories.hint': 'Crtica na traci je prethodni obilazak.',
  'fiveS.insights.categories.hintFirst': 'Prvi obilazak ovog centra — nema se još s čim uporediti.',
  'fiveS.insights.history.title': 'Kretanje ovog radnog centra',
  'fiveS.insights.history.hint': 'Posljednjih dvanaest obilazaka, najstariji prvi.',
  'fiveS.insights.history.tooShort': 'Treba još jedan obilazak da bi se vidio smjer.',
  'fiveS.insights.criteria.title': 'Šta prvo popraviti',
  'fiveS.insights.criteria.hint': 'Kriterijumi ocijenjeni ispod {target} skale, najslabiji prvi.',
  'fiveS.insights.empty': 'Nema ocjena na ovom obilasku.',
  'fiveS.insights.series.score': 'Ocjena',
  'fiveS.insights.rank.title': 'Mjesto u pogonu ovog mjeseca',
  'fiveS.insights.rank.value': '{position}. od {of}',
  'fiveS.insights.rank.plantAverage': 'Prosjek pogona',
  'fiveS.insights.rank.hint': 'Razlika u odnosu na prosjek: {difference}.',

  // The machine's place is counted among the machines of the plants the
  // reader sees; see `home.fiveS.plant.*` for why the label follows that.
  'fiveS.insights.rank.title.several': 'Mjesto u vašim pogonima ovog mjeseca',
  'fiveS.insights.rank.title.all': 'Mjesto u firmi ovog mjeseca',
  'fiveS.insights.rank.plantAverage.several': 'Prosjek vaših pogona',
  'fiveS.insights.rank.plantAverage.all': 'Prosjek firme',

  // ---- /5s: which machines still need a walk this month -----------
  //
  // The board the 5S tab opens on. *Mašina* rather than *radni centar*
  // on it: that is what people on the floor call the thing they walk up to.
  'fiveS.page.title': '5S obilasci',
  'fiveS.page.subtitle': 'Koje mašine još treba obići ovaj mjesec i kako su prošle obiđene.',
  'fiveS.tabs.aria': 'Prikaz 5S obilazaka',
  'fiveS.tabs.month': 'Ovaj mjesec',
  'fiveS.tabs.all': 'Svi obilasci',
  'fiveS.board.month.previous': 'Prethodni mjesec',
  'fiveS.board.month.next': 'Sljedeći mjesec',
  'fiveS.board.month.current': 'Na ovaj mjesec',
  // Counted by the machines, whose noun the number inflects: *od 2 mašine*,
  // *od 5 mašina*. The walked count leads and agrees with nothing.
  'fiveS.board.coverage.one': '{walked} od {count} mašine obiđeno',
  'fiveS.board.coverage.few': '{walked} od {count} mašine obiđeno',
  'fiveS.board.coverage.many': '{walked} od {count} mašina obiđeno',
  'fiveS.board.coverage.none': 'Nema mašina za obilazak',
  'fiveS.board.coverage.label': 'Obiđene mašine',
  'fiveS.board.left': 'Za obilazak: {count}',
  'fiveS.board.allDone': 'Sve mašine su obiđene.',
  'fiveS.board.drafts': 'U toku: {count}',
  'fiveS.board.average': 'Prosjek: {value}',
  'fiveS.board.withoutTemplate': 'Bez obrasca: {count}',
  'fiveS.board.withoutTemplate.help':
    'Mašina bez 5S obrasca ne može se ocijeniti, pa se ne broji. Obrazac dodaje administrator.',
  'fiveS.board.past': 'Ovo je raniji mjesec. Novi obilazak se uvijek upisuje u tekući mjesec.',
  'fiveS.board.filter.aria': 'Koje mašine prikazati',
  'fiveS.board.filter.all': 'Sve',
  'fiveS.board.filter.due': 'Za obilazak',
  'fiveS.board.filter.walked': 'Obiđene',
  'fiveS.board.filter.mine': 'Moje mašine',
  'fiveS.board.plant.progress': '{walked} / {total} obiđeno',
  'fiveS.board.col.machine': 'Mašina',
  'fiveS.board.col.status': 'Stanje',
  'fiveS.board.col.score': 'Ocjena',
  'fiveS.board.col.trend': 'Zadnjih 6 mjeseci',
  'fiveS.board.col.action': 'Radnja',
  'fiveS.board.mine': 'vaša',
  'fiveS.board.status.due': 'Za obilazak',
  'fiveS.board.status.draft': 'U toku',
  'fiveS.board.status.walked': 'Obiđena',
  'fiveS.board.status.noTemplate': 'Nema obrasca',
  'fiveS.board.lastSeen': 'Zadnja:',
  'fiveS.board.never': 'Bez obilaska u zadnjih 6 mjeseci',
  'fiveS.board.walks': 'obilazaka: {count}',
  'fiveS.board.openWalk': 'Otvori obilazak: {name}',
  'fiveS.board.trend.aria': 'Ocjena po mjesecima — {points}',
  'fiveS.board.trend.none': 'bez obilaska',
  'fiveS.board.delta.up': 'Bolje za {value} nego prethodni put',
  'fiveS.board.delta.down': 'Slabije za {value} nego prethodni put',
  'fiveS.board.delta.same': 'Isto kao prethodni put',
  'fiveS.board.evaluate': 'Ocijeni',
  'fiveS.board.evaluateMachine': 'Ocijeni mašinu {name}',
  'fiveS.board.continue': 'Nastavi',
  'fiveS.board.continueMachine': 'Nastavi započeti obilazak: {name}',
  'fiveS.board.empty.title': 'Nema mašina u vašim pogonima',
  'fiveS.board.empty.description':
    'Mašine dodaje administrator u Administraciji, pod Radni centri. Ovdje se pojave čim postoje.',
  'fiveS.board.empty.allWalked': 'Sve mašine su obiđene za {month}.',
  'fiveS.board.empty.noneWalked': 'Za {month} još nijedna mašina nije obiđena.',
  'fiveS.board.empty.mine': 'Nijedna mašina nije dodijeljena vama.',
  'fiveS.board.empty.showAll': 'Prikaži sve mašine',

  // ---- The walk: which machine, and how far along ---------------
  'fiveS.walk.subtitle': 'Izaberite mašinu, ocijenite svaki kriterijum i predajte.',
  'fiveS.picker.title': 'Koju mašinu ocjenjujete?',
  'fiveS.picker.search': 'Traži po nazivu, šifri ili pogonu…',
  'fiveS.picker.none': 'Nijedna mašina ne odgovara pretrazi.',
  'fiveS.picker.due': 'Za obilazak ovaj mjesec',
  'fiveS.picker.walked': 'Već obiđene ovaj mjesec',
  'fiveS.picker.noTemplate': 'Bez obrasca — ne mogu se ocijeniti',
  'fiveS.picker.continueDraft': 'nastavite započeti obilazak',
  'fiveS.picker.change': 'Druga mašina',
  'fiveS.picker.last': 'zadnja ocjena',
  'fiveS.scan.button': 'Skeniraj mašinu',
  'fiveS.scan.title': 'Skenirajte QR naljepnicu mašine',
  'fiveS.scan.hint': 'Usmjerite kameru na naljepnicu; mašina se otvara sama.',
  'fiveS.scan.opening': 'Tražim mašinu…',
  'fiveS.scan.again': 'Pokušaj ponovo',

  // ---- One walk, read back ---------------------------------------
  'fiveS.detail.all': 'Svi kriterijumi',
  // The band a score is in, written beside its colour rather than left to it.
  'fiveS.tone.good': 'Na cilju',
  'fiveS.tone.warn': 'Ispod cilja',
  'fiveS.tone.bad': 'Daleko ispod cilja',
  'fiveS.insights.criteria.none': 'Svi kriterijumi su na cilju ili iznad njega.',
  'fiveS.list.empty.hint': 'Svaki obilazak se pojavi ovdje čim se sačuva, najnoviji prvi.',
  'fiveS.list.evaluator': 'Ocjenjivač',
} as const

export type FiveSSlice = Record<keyof typeof fiveS, string>
